import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { ArcElement, CategoryScale, Chart as ChartJS, Tooltip } from 'chart.js';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Box,
  Divider,
  Grid,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import CustomButton from 'components/Button';
import { getChartColors } from 'components/GlobalStyles/styles';
import GridItem from 'components/GridItem';
import { JOB_TYPE_LABELS } from 'utils/constants';

import { ItemListContainer, OverviewTeamBox, TeamGridItem } from './styles';
import { TeamsOverviewProps } from './types';

ChartJS.register(ArcElement, Tooltip, CategoryScale);

const chartOptions = {
  plugins: {
    legend: {
      display: false,
    },
    datalabels: {
      display: false,
    },
  },
};

/**
 *
 * Page component for Teams Overview section of the application.
 * Displays a list of teams with their resource utilization breakdown and a doughnut chart visualizing the breakdown.
 * Allows navigation to team details page on clicking "View Detail" button.
 *
 * NAVIGATION URL: /teams
 */
const TeamsOverview = ({ resourceData, isFetching, dateFilters }: TeamsOverviewProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const chartColours = getChartColors(theme);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [selectedTeam, setSelectedTeam] = React.useState<string | null>(null);
  const [displayUnit, setDisplayUnit] = React.useState<'number' | 'percent'>('number');

  const filteredResourceData = resourceData
    .filter((teamData) => Object.keys(teamData.utilisation).length > 0)
    .map((teamData) => ({
      team_id: teamData.teams.team_id,
      team_name: teamData.teams.team_name,
      quoted_billable_hours: teamData.utilisation.quoted || 0,
      quoted_time_billable_hours: teamData.utilisation.timebilled || 0,
      productive_hours: teamData.utilisation.internal || 0,
      total_hours:
        (teamData.utilisation.quoted || 0) +
        (teamData.utilisation.timebilled || 0) +
        (teamData.utilisation.internal || 0),
    }));

  const selectedTeamData = selectedTeam ? filteredResourceData.find((team) => team.team_id === selectedTeam) : null;

  const overallBreakDown = [
    filteredResourceData.reduce((acc, team) => acc + team.quoted_billable_hours, 0),
    filteredResourceData.reduce((acc, team) => acc + team.quoted_time_billable_hours, 0),
    filteredResourceData.reduce((acc, team) => acc + team.productive_hours, 0),
  ];

  const allData = selectedTeamData
    ? [
        selectedTeamData.quoted_billable_hours,
        selectedTeamData.quoted_time_billable_hours,
        selectedTeamData.productive_hours,
      ]
    : overallBreakDown;

  // Filter out zero values for chart
  const filtered = allData
    .map((value, idx) => ({
      value,
      label: JOB_TYPE_LABELS[idx],
      color: chartColours[idx],
    }))
    .filter((item) => item.value !== 0);

  const chartData = {
    labels: filtered.map((item) => item.label),
    datasets: [
      {
        label: 'Hours',
        data: filtered.map((item) => item.value),
        backgroundColor: filtered.map((item) => item.color),
        borderWidth: 4,
      },
    ],
  };

  const navigateToDetails = (id: string) => {
    navigate(`/teams/overview/${id}`, { state: { dateFilters } });
  };

  // Rendering of Grid Items
  const renderEmptyState = () => {
    return (
      <Box sx={{ height: '44vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No data available.
        </Typography>
      </Box>
    );
  };

  const renderGridItems = () => {
    return filteredResourceData.map((team) => {
      return (
        <TeamGridItem
          container
          data-testid={`team-grid-item-${team.team_id}`}
          key={team.team_id}
          onClick={(event) => {
            event.stopPropagation();
            setSelectedTeam(team.team_id.toString());
          }}
          selected={selectedTeam === team.team_id.toString()}>
          <GridItem title={team.team_name} seenOrIsHeading={false} breakpoint={isMobileScreen ? 5 : 4} />
          <GridItem title={team.quoted_billable_hours} seenOrIsHeading={false} breakpoint={2} />
          <GridItem title={team.quoted_time_billable_hours} seenOrIsHeading={false} breakpoint={2} />
          <GridItem title={team.productive_hours} seenOrIsHeading={false} breakpoint={2} />
          <GridItem title={team.total_hours} seenOrIsHeading={false} breakpoint={isMobileScreen ? 1 : 2} />
        </TeamGridItem>
      );
    });
  };

  const renderGirdSkeletons = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TeamGridItem container key={index}>
        <GridItem
          title={<Skeleton variant="text" width={100} />}
          seenOrIsHeading={false}
          breakpoint={isMobileScreen ? 5 : 4}
        />
        <GridItem title={<Skeleton variant="text" width={50} />} seenOrIsHeading={false} breakpoint={2} />
        <GridItem title={<Skeleton variant="text" width={50} />} seenOrIsHeading={false} breakpoint={2} />
        <GridItem title={<Skeleton variant="text" width={50} />} seenOrIsHeading={false} breakpoint={2} />
      </TeamGridItem>
    ));
  };

  // Rendering of Graph
  const renderTeamBreakdownGraph = () => {
    const totalValue = chartData.datasets[0].data.reduce((sum, val) => sum + val, 0);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <Box sx={{ height: '44vh', padding: theme.spacing(4) }}>
          <Doughnut data={chartData} options={chartOptions} />
        </Box>

        {chartData.labels.length > 0 && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginBottom: theme.spacing(2),
            }}>
            <ToggleButtonGroup
              color="primary"
              value={displayUnit}
              exclusive
              onChange={(_, newUnit) => newUnit && setDisplayUnit(newUnit)}
              aria-label="Display Unit"
              size="small">
              <ToggleButton value="number">Value</ToggleButton>
              <ToggleButton value="percent">% age</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Total row */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            border: '2px solid',
            borderRadius: '4px',
            padding: theme.spacing(2),
            paddingY: theme.spacing(2),
            marginTop: theme.spacing(2),
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
          }}>
          <Typography
            variant="h5"
            sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.primary, fontWeight: 700 }}>
            Total
          </Typography>
          <Typography variant="h5" sx={{ marginLeft: 'auto', color: theme.palette.text.primary, fontWeight: 700 }}>
            {displayUnit === 'number' ? totalValue : '100%'}
          </Typography>
        </Box>
        {chartData.labels.map((label, index) =>
          chartData.datasets[0].data[index] !== 0 ? (
            <Box
              key={index}
              sx={{
                width: '100%',
                display: 'flex',
                border: '1px solid',
                borderRadius: '4px',
                alignItems: 'center',
                padding: theme.spacing(2),
                paddingY: theme.spacing(2),
                marginTop: theme.spacing(2),
                borderColor: theme.palette.divider,
              }}>
              <Typography
                variant="h5"
                sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.primary }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '2px',
                    display: 'inline-block',
                    marginRight: theme.spacing(1),
                    backgroundColor: chartData.datasets[0].backgroundColor[index],
                  }}
                />
                {label}
              </Typography>
              <Typography variant="h5" sx={{ marginLeft: 'auto', color: theme.palette.text.primary }}>
                {displayUnit === 'percent'
                  ? ` ${((chartData.datasets[0].data[index] / totalValue) * 100).toFixed(2)}%`
                  : chartData.datasets[0].data[index]}
              </Typography>
            </Box>
          ) : null,
        )}
      </Box>
    );
  };

  const renderDoughnutSkeleton = () => {
    return (
      <Box sx={{ height: '44vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Skeleton variant="circular" width={400} height={400} />
      </Box>
    );
  };

  // TeamOverview
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        gap: theme.spacing(2),
        flexDirection: { xs: 'column', md: 'row' },
      }}>
      <OverviewTeamBox onClick={() => setSelectedTeam(null)}>
        <ItemListContainer data-testid="itemListContainer">
          {/* Grid Header */}
          <Grid
            container
            display="flex"
            flexDirection="row"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
            <GridItem title="Team" seenOrIsHeading={false} breakpoint={isMobileScreen ? 5 : 4} />
            <GridItem title="Quoted" seenOrIsHeading={false} breakpoint={2} />
            <GridItem title="Time Billed" seenOrIsHeading={false} breakpoint={2} />
            <GridItem title="Internal" seenOrIsHeading={false} breakpoint={2} />
            <GridItem title="Total" seenOrIsHeading={false} breakpoint={isMobileScreen ? 1 : 2} />
          </Grid>

          {/* Grid Items */}
          {isFetching
            ? renderGirdSkeletons()
            : filteredResourceData.length > 0
            ? renderGridItems()
            : renderEmptyState()}
        </ItemListContainer>

        {/* Bottom Grid Item */}
        <Box sx={{ maxWidth: '960px', padding: theme.spacing(2) }}>
          <TeamGridItem container>
            <GridItem title="Total" seenOrIsHeading={false} breakpoint={isMobileScreen ? 5 : 4} />
            {JOB_TYPE_LABELS.map((label) => (
              <GridItem
                key={label}
                title={overallBreakDown[JOB_TYPE_LABELS.indexOf(label)]}
                seenOrIsHeading={false}
                breakpoint={2}
              />
            ))}
            <GridItem
              title={overallBreakDown.reduce((sum, v) => sum + v, 0)}
              seenOrIsHeading={false}
              breakpoint={isMobileScreen ? 1 : 2}
            />
          </TeamGridItem>
        </Box>
      </OverviewTeamBox>

      {/* Graph Section */}
      <OverviewTeamBox>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingY: theme.spacing(1),
          }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ color: theme.palette.text.primary, paddingTop: theme.spacing(1) }}>
            {selectedTeamData ? `${selectedTeamData.team_name} Breakdown` : 'Overall Team Breakdown'}
          </Typography>
          {selectedTeam && selectedTeamData && selectedTeamData?.total_hours > 0 && (
            <CustomButton
              variant="contained_grey"
              text="View Detail"
              onClick={() => navigateToDetails(selectedTeam.toString())}
              width="medium"
              customSx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
              endIcon={<ArrowForwardIosIcon />}
            />
          )}
        </Box>
        <Divider />

        {isFetching
          ? renderDoughnutSkeleton()
          : filteredResourceData.length > 0
          ? renderTeamBreakdownGraph()
          : renderEmptyState()}
      </OverviewTeamBox>
    </Box>
  );
};
export default TeamsOverview;
